import type { SolverInput, SolverOutput } from '../model/types';
import { MinHeap } from './minHeap';

/**
 * Any-angle shortest-path search (Theta*) over the elevation grid, from the origin outwards.
 *
 * A segment is flyable if the glide line (altitude changes 1 m per `glideRatio` m flown) stays above
 * terrain + clearance along its whole length. Shortest path = best altitude, so every reached cell gets
 * the best possible altitude, and paths bend around ridges instead of forming a plain cone.
 *
 * 'fly':  h = best altitude you can arrive at each cell with (maximised).
 * 'hike': h = lowest altitude you must be at over each cell to still glide to the landing (minimised).
 *         It is the same search run backwards: altitude grows by 1 m per `glideRatio` m away from the landing.
 *
 * Pure and self-contained: runs in a Web Worker.
 */
export function solveCone(input: SolverInput): SolverOutput {
  const { mode, elev, size, cellSize: cs, baseAlt, glideRatio: gr, clearance, radiusCells, maxAlt } = input;
  const fly = mode === 'fly';
  const dir = fly ? -1 : 1; // sign of altitude change moving away from the origin
  const n = size * size;
  const N = (size - 1) / 2;
  const origin = N * size + N;
  const h = new Float32Array(n).fill(NaN);
  const need = new Float32Array(n).fill(NaN);
  const g = new Float32Array(n).fill(Infinity);
  const parent = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const r2 = radiusCells * radiusCells;
  // key is dir * h so 'fly' pops the highest, 'hike' the lowest altitude first
  const heap = new MinHeap();

  const dist = (a: number, b: number) =>
    Math.hypot((a % size) - (b % size), ((a / size) | 0) - ((b / size) | 0)) * cs;

  // Straight glide line from cell a (altitude ha) to cell b, climbing (d = 1) or sinking (d = -1).
  // Returns the line's altitude at b, or NaN if it hits terrain.
  function glide(a: number, b: number, ha = h[a], d = dir) {
    const ax = a % size;
    const ay = (a / size) | 0;
    const dx = (b % size) - ax;
    const dy = ((b / size) | 0) - ay;
    const len = Math.hypot(dx, dy) * cs;
    const steps = Math.ceil(Math.hypot(dx, dy) * 2);
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const ci = Math.round(ay + dy * t) * size + Math.round(ax + dx * t);
      if (ci === a) continue; // terrain under the source cell is already accounted for
      if (!fly && ci === b) continue; // hike: b may be a slope above the line, i.e. a takeoff
      if (ha + (d * len * t) / gr < elev[ci] + clearance) return NaN;
    }
    return ha + (d * len) / gr;
  }

  h[origin] = baseAlt;
  g[origin] = 0;
  heap.push(origin, dir * baseAlt);
  while (heap.size) {
    const c = heap.pop();
    if (closed[c]) continue;
    closed[c] = 1;
    if (!fly && h[c] > maxAlt) break; // nothing in the grid is high enough to start from here
    const cx = c % size;
    const cy = (c / size) | 0;
    const pc = parent[c];
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        if (!ox && !oy) continue;
        const nx = cx + ox;
        const ny = cy + oy;
        if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
        if ((nx - N) * (nx - N) + (ny - N) * (ny - N) > r2) continue;
        const ni = ny * size + nx;
        if (closed[ni]) continue;
        // Theta*: try a straight line from our parent first (any-angle path)
        let par = pc;
        let alt = pc >= 0 ? glide(pc, ni) : NaN;
        if (Number.isNaN(alt)) {
          par = c;
          alt = glide(c, ni);
        }
        if (Number.isNaN(alt)) continue;
        const needAlt = alt;
        // hike: to keep flying back from here you must clear this cell's terrain
        if (!fly) alt = Math.max(alt, elev[ni] + clearance);
        if (Number.isNaN(h[ni]) || dir * alt < dir * h[ni]) {
          h[ni] = alt;
          need[ni] = needAlt;
          g[ni] = g[par] + dist(par, ni);
          parent[ni] = par;
          heap.push(ni, dir * alt);
        }
      }
    }
  }

  // hike: the backward search assumes you are exactly at the minimum altitude over each cell, so
  // its paths hug the slopes. A takeoff actually starts from the ground, often
  // well above that, so shortcut straight to the farthest-along cell on the path we can glide to.
  const launchG = new Float32Array(n).fill(Infinity);
  const launchParent = new Int32Array(n).fill(-1);
  if (!fly) {
    launchG[origin] = 0;
    for (let x = 0; x < n; x++) {
      const launch = elev[x];
      if (x === origin || !(launch >= need[x])) continue;
      for (let a = parent[x]; a >= 0; a = parent[a]) {
        const total = dist(x, a) + g[a];
        if (total < launchG[x] && glide(x, a, launch, -1) >= h[a]) {
          launchG[x] = total;
          launchParent[x] = a;
        }
      }
    }
  }
  return { h, need, g, parent, launchG, launchParent };
}
